import type { ReactElement } from 'react';
import type { GetServerSideProps, GetServerSidePropsResult as GSSPResult } from 'next';
import Head from 'next/head';
import { getImageProps } from 'next/image';
import { useQuery } from '@tanstack/react-query';
import type { WebappResponse } from '@eventbrite/event-listing-service-client';
import { EntityType } from '@eventbrite/cerberus-service-pysoa-client';
import { EmbeddedMetricsLogger } from '@eventbrite/openapi-service';
import { Stage, stageFromString } from '@eventbrite/webapp-constants';
import { getLocaleFromRequest } from '@eventbrite/webapp-locales';
import { MetaTags, OpenGraphTags, TwitterTags, TwitterCard } from '@eventbrite/webapp-public-content';

import { EVENT_STATUS } from '@/types';

import { logger } from '@/utils/logger';
import { buildListingsEventListingServiceClient } from '@/clients/eventListing';
import { setCacheControl } from '@/utils/cacheControl';
import setCanonicalUrl from '@/utils/canonicalUrl';
import { type EventListingError, handle404Error, handleError } from '@/utils/error-handling';
import { SLUG_ROUTE_PATTERN, webappMetricsLogger } from '@/utils/webapp-metrics';
import { getSoaTokenFromHeaders, getUserIdFromHeaders } from '@/utils/ebRequestHeaders';
import {
  createResilientCerberusClient,
  createResilientEventListingServiceClient,
} from '@/utils/resilientDependencyClients';
import { Header } from '@/components/Header';
import { ProtectedEventGate } from '@/components/ProtectedEventGate';
import { getBaseDeepLinkAppUrl } from '@/components/SEOTags';
import { getSocialImageUrl, SOCIAL_IMAGE_HEIGHT, SOCIAL_IMAGE_WIDTH } from '@/components/SEOTags/socialImageMetadata';
import { useEventDescription, useEventTitle } from '@/hooks';

import { QUERY_PARAMS } from '../../common/constants';
import { getLocaleMessages } from '../../i18n/locale';

import { sanitizeAndValidateEventId } from './validation/slug';
import { DEFAULT_LOCALE } from '@eventbrite/webapp-locales/dist/constants';
import type { ListingPageServerProps, ListingPageProps } from './listingPageTypes';
import type { ProtectedEventInfo } from './listingPageTypes';
import { buildListingPagePropsFromContext } from './utils/buildListingPageProps';
import { EventListingPage } from '@/components/EventListingPage/EventListingPage';
import { EditorRefreshBannerContent } from '@/components/NotificationBanner/notificationBannerUseCases';
import { NotificationBannerFrame } from '@/components/NotificationBanner';
import type { BasicInfo } from '@/types';
import { getUserDataQueryOptions } from '@/hooks/useUserData/useUserData';
import { fetchFreshEventContext } from '@/utils/fetchFreshEventContext';
import { getGlobalHeaderServerSideProps } from '@eventbrite/consumer-header.global-header/server';
import { getCerberusClient, hasEventPermission } from './api/[eventId]/cerberus';
import { EVENT_DETAILS_READ_PERMISSION } from './api/[eventId]/constants';
import { isNonEmptyString } from '@/utils/apiRouteUtils';
import { dependencyMetrics } from './api/[eventId]/eventRouteUtils';
import { TimeoutError } from '@/utils/resiliency';
import { fetchMoreOrganizerEvents } from '@/services/moreOrganizerEvents';
import { isOrganizerEventsSsrEnabled } from '@/utils/organizerEventsSsr';
import { isCreatorEventSearchForOrganizerEventsEnabled } from '@/utils/organizerEventsRetrieval';
import type { OrganizerEventResponse } from '@/components/MoreOrganizerEvents/types';

type GetServerSidePropsResult = GetServerSideProps<ListingPageServerProps>;
const PROTECTED_EDITOR_CONTEXT_QUERY_KEY = 'protected-editor-event-context';
const CERBERUS_TIMEOUT_MS = 2500;
const ELS_CONTEXT_TIMEOUT_MS = 5000;
const ELS_CONTEXT_TIMEOUT_MESSAGE = 'Event listing context timed out';

/** Hydration-call budget for the organizer-events SSR fetch. Sized to the
 *  event-hydration-service P95 (30–50ms cache-hot, >95% cache hit rate). */
const ORGANIZER_EVENTS_SSR_HYDRATION_TIMEOUT_MS = 150;

/** Overall TTFB budget for the SSR organizer-events fetch (SOA proxy +
 *  hydration combined). Caps worst-case page TTFB contribution at this value
 *  regardless of the SOA proxy client's 3s default. Sized to capture roughly
 *  p75 of `call.event.get_organizer_future_events` (p50 131ms, p75 216ms);
 *  renders past this budget fall through to the client-side lazy fetch. */
const ORGANIZER_EVENTS_SSR_DEADLINE_MS = 500;

function hasUsableProtectedEditorContext(context: WebappResponse | null | undefined): context is WebappResponse {
  if (!context?.basicInfo) {
    return false;
  }

  const protectedEditorData = [
    context.structuredContent != null,
    context.salesStatus != null,
    context.goodToKnow != null,
    context.taxonomies != null,
    context.performers != null,
    context.presenterInfo != null,
    Array.isArray(context.tags),
    Array.isArray(context.collections),
  ];

  return protectedEditorData.some(Boolean);
}

const log404Error = (cause: string, eventId: string | number | null, additionalData?: Record<string, unknown>) => {
  logger.warn('Render 404', {
    cause,
    eventId: eventId?.toString() || 'unknown',
    ...additionalData,
  });
};

/**
 * Checks if the current request is from a logged-in user who has editor
 * permissions on the given event via Cerberus. Returns `true` only when
 * credentials are present AND Cerberus grants `event.details:read`.
 */
const isRequestFromEditor = async (
  headers: Record<string, string | string[] | undefined>,
  eventId: string,
): Promise<boolean> => {
  const userId = getUserIdFromHeaders(headers) ?? '';
  const soaToken = getSoaTokenFromHeaders(headers) ?? '';

  if (!isNonEmptyString(userId) || !isNonEmptyString(soaToken)) {
    return false;
  }

  try {
    const resilientCerberus = createResilientCerberusClient(getCerberusClient(), dependencyMetrics, {
      permissions: CERBERUS_TIMEOUT_MS,
      user: CERBERUS_TIMEOUT_MS,
    });
    const permissionsResponse = await resilientCerberus.calculatePermissions({
      context: { auth_token: soaToken },
      body: {
        permissions: [EVENT_DETAILS_READ_PERMISSION],
        entities: [{ type: EntityType.EVENT, id: eventId }],
      },
    });
    return hasEventPermission(permissionsResponse, eventId, EVENT_DETAILS_READ_PERMISSION);
  } catch (error) {
    logger.warn('Slug: Cerberus editor check failed, treating as non-editor', { eventId, error });
    return false;
  }
};

export const getServerSideProps = (async ({ res, params, query, req }) => {
  const stage = stageFromString(process.env.STAGE || Stage.TEST);
  const startTime = Date.now();
  const { slug } = params || {};
  const eventId = sanitizeAndValidateEventId(slug);
  const keepTld = query.keep_tld === 'true';
  const skipRedirection = stage === Stage.LOCAL || query.skip_redirection === 'true';
  const ebHost = (req.headers['eb-host'] || req.headers['host']) as string;

  let _globalHeaderPropsCache: Awaited<ReturnType<typeof getGlobalHeaderServerSideProps>> | undefined;
  const getGlobalHeaderPropsOnce = async () => {
    if (_globalHeaderPropsCache === undefined) {
      _globalHeaderPropsCache = await getGlobalHeaderServerSideProps({ req, res, query, resolvedUrl: '/' });
    }
    return _globalHeaderPropsCache;
  };

  // Initial locale comes from the request (or defaults), but we only
  // fetch translation messages once we know the final locale.
  const localeFromRequest = getLocaleFromRequest(req) || DEFAULT_LOCALE;
  const locale = localeFromRequest;
  let messages: Record<string, string> = {};
  if (!eventId) {
    log404Error('INVALID_EVENT_ID', null, {
      slug: typeof slug === 'string' ? slug : Array.isArray(slug) ? slug[0] : 'undefined',
    });
    webappMetricsLogger.logRenderError(SLUG_ROUTE_PATTERN, 'Invalid event ID', startTime, 404);
    try {
      messages = await getLocaleMessages(locale);
    } catch (error) {
      logger.error(`--locale-- Failed to get messages for locale ${locale} for invalid event`, {
        error,
      });
    }
    return handle404Error(res, locale, messages, await getGlobalHeaderPropsOnce(), req.headers);
  }
  // Embedded Metrics Logger will send AWS Metrics under this namespace
  const metrics = new EmbeddedMetricsLogger('dependency-metrics');
  const eventListingServiceClient = createResilientEventListingServiceClient(
    buildListingsEventListingServiceClient(stage, metrics),
    metrics,
    ELS_CONTEXT_TIMEOUT_MS,
  );
  const getEventContextWebWithTimeout = async (ebSoaToken?: string) => {
    try {
      return await eventListingServiceClient.getEventContextWeb(eventId.toString(), {}, ebSoaToken);
    } catch (error) {
      if (error instanceof TimeoutError) {
        throw Object.assign(error, {
          statusCode: 503,
          error: ELS_CONTEXT_TIMEOUT_MESSAGE,
        });
      }
      throw error;
    }
  };

  /**
   * Split-gated SSR fetch for the "More events from this organizer" section.
   * Returns `null` when the gate is off, the organizer is missing, the overall
   * deadline fires, or the upstream call errors — letting the client-side
   * lazy fetch populate the section. The overall deadline caps the page's
   * worst-case TTFB contribution from this fetch (see
   * `ORGANIZER_EVENTS_SSR_DEADLINE_MS`); the per-step hydration timeout
   * (`ORGANIZER_EVENTS_SSR_HYDRATION_TIMEOUT_MS`) still applies inside it.
   */
  const fetchOrganizerEventsForSsrOrNull = async (context: WebappResponse): Promise<OrganizerEventResponse | null> => {
    const organizerId = context.basicInfo?.organizer?.id;
    if (!organizerId) {
      return null;
    }

    const ssrEnabled = await isOrganizerEventsSsrEnabled(eventId.toString(), context.basicInfo?.organizationId);
    if (!ssrEnabled) {
      return null;
    }

    const useCreatorEventSearch = await isCreatorEventSearchForOrganizerEventsEnabled({
      eventId: eventId.toString(),
      organizerId,
    });

    let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
    const deadlinePromise = new Promise<null>((resolve) => {
      deadlineTimer = setTimeout(() => resolve(null), ORGANIZER_EVENTS_SSR_DEADLINE_MS);
    });

    try {
      const result = await Promise.race([
        fetchMoreOrganizerEvents({
          eventId: eventId.toString(),
          organizerId,
          soaToken: getSoaTokenFromHeaders(req.headers),
          stage,
          metrics,
          hydrationTimeoutMs: ORGANIZER_EVENTS_SSR_HYDRATION_TIMEOUT_MS,
          useCreatorEventSearch,
        }),
        deadlinePromise,
      ]);
      if (result === null) {
        logger.info('Slug: organizer-events SSR fetch hit overall deadline, falling back to client', { eventId });
      }
      return result;
    } catch (error) {
      if (error instanceof TimeoutError) {
        logger.info('Slug: organizer-events SSR fetch timed out, falling back to client', { eventId });
      } else {
        logger.warn('Slug: organizer-events SSR fetch failed, falling back to client', { eventId, error });
      }
      return null;
    } finally {
      if (deadlineTimer) {
        clearTimeout(deadlineTimer);
      }
    }
  };

  /**
   * Helper: given a successful ELS context, handle all rendering paths
   * (draft-preview, protected, redirect, normal).
   */
  const handleContext = async (
    context: WebappResponse,
    isAuthenticated: boolean,
  ): Promise<GSSPResult<ListingPageServerProps>> => {
    if (!context || !context.basicInfo) {
      if (!context) {
        log404Error('SERVICE_RETURNED_NULL', eventId);
      } else {
        log404Error('MISSING_BASIC_INFO', eventId);
      }
      webappMetricsLogger.logRenderError(SLUG_ROUTE_PATTERN, 'Event not found or invalid status', startTime, 404);
      messages = await getLocaleMessages(locale);
      return handle404Error(res, locale, messages, await getGlobalHeaderPropsOnce(), req.headers);
    }

    if (context.basicInfo.status === EVENT_STATUS.DRAFT || context.basicInfo.status === EVENT_STATUS.UNSAVED) {
      if (isAuthenticated) {
        logger.info('Slug: rendering draft event in preview mode for editor', { eventId });
        setCacheControl(res, context, { forceNoCache: true });
        const baseProps = await buildListingPagePropsFromContext({ req, res, query, context });
        webappMetricsLogger.logRenderSuccess(SLUG_ROUTE_PATTERN, startTime, 200);
        return {
          props: {
            ...baseProps,
            isPreview: true,
          },
        };
      }

      log404Error(
        context.basicInfo.status === EVENT_STATUS.DRAFT ? 'EVENT_STATUS_DRAFT' : 'EVENT_STATUS_UNSAVED',
        eventId,
      );
      webappMetricsLogger.logRenderError(SLUG_ROUTE_PATTERN, 'Event not found or invalid status', startTime, 404);
      messages = await getLocaleMessages(locale);
      return handle404Error(res, locale, messages, await getGlobalHeaderPropsOnce(), req.headers);
    }

    if (context.basicInfo.isProtected) {
      if (await isRequestFromEditor(req.headers, eventId.toString())) {
        try {
          const soaToken = getSoaTokenFromHeaders(req.headers);
          const authContext = await getEventContextWebWithTimeout(soaToken);
          if (authContext?.basicInfo && !authContext.basicInfo.isProtected) {
            setCacheControl(res, authContext, { forceNoCache: true });
            setCanonicalUrl(res, authContext);
            const initialMoreOrganizerEvents = await fetchOrganizerEventsForSsrOrNull(authContext);
            const baseProps = await buildListingPagePropsFromContext({
              req,
              res,
              query,
              context: authContext,
              initialMoreOrganizerEvents,
            });
            webappMetricsLogger.logRenderSuccess(SLUG_ROUTE_PATTERN, startTime, 200);
            return { props: baseProps };
          }
        } catch (retryError) {
          logger.warn('Slug: authenticated ELS retry failed for editor on protected event', {
            eventId,
            error: retryError,
          });
        }
      }

      const slugStr = typeof slug === 'string' ? slug : Array.isArray(slug) && slug[0] ? slug[0] : '';
      const protectedEvent = buildProtectedEventInfo(context, slugStr);
      setCacheControl(res, context, { forceNoCache: query.forceNoCache === 'true' });
      setCanonicalUrl(res, context);
      const baseProps = await buildListingPagePropsFromContext({ req, res, query, context });
      webappMetricsLogger.logRenderSuccess(SLUG_ROUTE_PATTERN, startTime, 200);
      return {
        props: {
          ...baseProps,
          context: null,
          protectedEvent,
        },
      };
    }

    const forcedError =
      query[QUERY_PARAMS.FORCE_ERROR] && !isNaN(Number(query[QUERY_PARAMS.FORCE_ERROR]))
        ? {
            statusCode: Number(query[QUERY_PARAMS.FORCE_ERROR]),
            error: 'Forced Error by Query Param',
          }
        : null;

    if (forcedError) {
      webappMetricsLogger.logRenderError(SLUG_ROUTE_PATTERN, forcedError.error, startTime, forcedError.statusCode);
      try {
        messages = await getLocaleMessages(locale);
      } catch (error) {
        logger.error(`--locale-- Failed to get messages for locale ${locale} for invalid event`, {
          error,
        });
      }
      return handleError(res, forcedError, locale, messages, await getGlobalHeaderPropsOnce());
    }

    setCacheControl(res, context, { forceNoCache: query.forceNoCache === 'true' });
    setCanonicalUrl(res, context);

    // Redirection logic
    let canonicalUrl = buildUrl(context?.basicInfo?.url);
    if (canonicalUrl && !skipRedirection) {
      const canonicalUrlSlug = canonicalUrl.pathname.split('/').pop();

      if (keepTld && ebHost) {
        canonicalUrl = new URL(canonicalUrl.toString().replace(canonicalUrl.hostname, ebHost as string));
      }

      if (canonicalUrlSlug !== slug || canonicalUrl.hostname !== ebHost) {
        webappMetricsLogger.logRenderSuccess(SLUG_ROUTE_PATTERN, startTime, 301);
        return {
          redirect: {
            destination: canonicalUrl.toString(),
            statusCode: 301,
          },
        };
      }
    }

    const initialMoreOrganizerEvents = await fetchOrganizerEventsForSsrOrNull(context);
    const props = await buildListingPagePropsFromContext({
      req,
      res,
      query,
      context,
      initialMoreOrganizerEvents,
    });
    webappMetricsLogger.logRenderSuccess(SLUG_ROUTE_PATTERN, startTime, 200);

    return { props };
  };

  try {
    // First attempt: no SOA token so ELS can serve from its DynamoDB cache.
    const context = await getEventContextWebWithTimeout();
    return await handleContext(context, false);
  } catch (error) {
    const errorStatusCode = (error as EventListingError).statusCode || 500;

    // For 403/404 errors, check if this is an editor viewing a draft event.
    // Only then do we retry with the SOA token (which bypasses the ELS cache).
    if (errorStatusCode === 403 || errorStatusCode === 404) {
      if (await isRequestFromEditor(req.headers, eventId.toString())) {
        try {
          const soaToken = getSoaTokenFromHeaders(req.headers);
          const context = await getEventContextWebWithTimeout(soaToken);
          return await handleContext(context, true);
        } catch (retryError) {
          const retryErrorStatusCode = (retryError as EventListingError).statusCode || 500;
          logger.warn('Slug: authenticated ELS retry failed for editor', { eventId, error: retryError });
          if (retryErrorStatusCode !== 403 && retryErrorStatusCode !== 404) {
            webappMetricsLogger.logRenderError(SLUG_ROUTE_PATTERN, retryError, startTime, retryErrorStatusCode);
            return handleError(res, retryError as EventListingError, locale, {}, await getGlobalHeaderPropsOnce());
          }
        }
      }

      log404Error(errorStatusCode === 403 ? 'SERVICE_THREW_403' : 'SERVICE_THREW_404', eventId, {
        slug: typeof slug === 'string' ? slug : Array.isArray(slug) ? slug.join(',') : 'unknown',
      });
      webappMetricsLogger.logRenderError(SLUG_ROUTE_PATTERN, error, startTime, 404);
      messages = await getLocaleMessages(locale);
      return handle404Error(res, locale, messages, await getGlobalHeaderPropsOnce(), req.headers);
    }
    webappMetricsLogger.logRenderError(SLUG_ROUTE_PATTERN, error, startTime, errorStatusCode);
    return handleError(res, error as EventListingError, locale, {}, await getGlobalHeaderPropsOnce());
  }
}) satisfies GetServerSidePropsResult;

function buildProtectedEventInfo(context: WebappResponse, slugStr: string): ProtectedEventInfo {
  const { basicInfo, gallery } = context;
  if (!basicInfo) {
    throw new Error('buildProtectedEventInfo requires context.basicInfo');
  }
  const status = basicInfo.status ?? '';
  const isEnded =
    status === EVENT_STATUS.ENDED || status === EVENT_STATUS.COMPLETED || status === EVENT_STATUS.CANCELLED;

  const images = gallery?.images as
    { croppedLogoUrl480?: string; croppedLogoUrl600?: string; croppedLogoUrl940?: string }[] | undefined;
  const primaryImageUrl = getSocialImageUrl(images);

  return {
    eventId: basicInfo.id!,
    slug: slugStr,
    name: basicInfo.name ?? '',
    summary: basicInfo.summary ?? '',
    startDate: basicInfo.startDate!,
    endDate: basicInfo.endDate!,
    hideStartDate: basicInfo.hideStartDate ?? false,
    hideEndDate: basicInfo.hideEndDate ?? false,
    locale: basicInfo.locale ?? 'en_US',
    url: basicInfo.url ?? '',
    isOnline: basicInfo.isOnline ?? false,
    isSeries: basicInfo.isSeries ?? false,
    ...(basicInfo.venue && {
      venue: {
        name: basicInfo.venue.name,
        address: {
          city: basicInfo.venue.address?.city,
          region: basicInfo.venue.address?.region,
        },
      },
    }),
    ...(basicInfo.organizer && {
      organizer: {
        id: basicInfo.organizer.id,
        name: basicInfo.organizer.name,
        url: basicInfo.organizer.url,
      },
    }),
    ...(primaryImageUrl && { backgroundImageUrl: primaryImageUrl }),
    isEnded,
  };
}

function buildUrl(url: string): URL | null {
  try {
    return new URL(url);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return null;
  }
}

export default function Page(props: ListingPageProps): ReactElement {
  const protectedEvent = props.protectedEvent;
  const seoBasicInfo = protectedEvent
    ? ({
        name: protectedEvent.name,
        summary: protectedEvent.summary ?? '',
        startDate: protectedEvent.startDate,
        endDate: protectedEvent.endDate,
        hideStartDate: protectedEvent.hideStartDate,
        hideEndDate: protectedEvent.hideEndDate ?? false,
        isOnline: protectedEvent.isOnline ?? false,
        isSeries: protectedEvent.isSeries ?? false,
        venue: protectedEvent.venue,
        organizer: {
          name: protectedEvent.organizer?.name ?? '',
        },
        isProtected: false,
      } as BasicInfo)
    : undefined;
  const title = useEventTitle(seoBasicInfo, props.locale);
  const description = useEventDescription(seoBasicInfo, props.locale);
  const { data: userData } = useQuery(
    getUserDataQueryOptions(protectedEvent?.eventId, protectedEvent?.isOnline, !!protectedEvent),
  );
  const { data: protectedEditorContext, isFetching: isRefreshingProtectedEditorContext } = useQuery({
    queryKey: [PROTECTED_EDITOR_CONTEXT_QUERY_KEY, protectedEvent?.eventId],
    queryFn: async () => {
      if (!protectedEvent?.eventId) {
        return null;
      }

      return fetchFreshEventContext(protectedEvent.eventId);
    },
    enabled: !!protectedEvent?.eventId && !!userData?.canManageEvent,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  if (protectedEvent && hasUsableProtectedEditorContext(protectedEditorContext)) {
    return (
      <EventListingPage
        {...props}
        context={protectedEditorContext}
        protectedEvent={undefined}
        skipFreshEditorContextFetch
      />
    );
  }

  if (protectedEvent) {
    const { globalHeaderProps, query } = props;
    const { name, url, backgroundImageUrl } = protectedEvent;
    const canonicalUrl = props.canonicalUrl && props.canonicalUrl.trim().length > 0 ? props.canonicalUrl : url;
    const {
      props: { src: socialImageUrl },
    } = getImageProps({
      alt: '',
      width: SOCIAL_IMAGE_WIDTH,
      height: SOCIAL_IMAGE_HEIGHT,
      src: backgroundImageUrl || '',
    });

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta name="description" content={description} />
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href={canonicalUrl} />
          <meta name="twitter:url" content={canonicalUrl} />
          {backgroundImageUrl && <meta property="og:image:width" content={SOCIAL_IMAGE_WIDTH.toString()} />}
          {backgroundImageUrl && <meta property="og:image:height" content={SOCIAL_IMAGE_HEIGHT.toString()} />}
        </Head>
        <MetaTags showDefaultDescription={false} />
        <OpenGraphTags
          url={canonicalUrl}
          title={name}
          description={description}
          imageUrl={socialImageUrl}
          type="events.event"
        />
        <TwitterTags
          description={description}
          title={name}
          imageUrl={socialImageUrl}
          iosAppUrl={getBaseDeepLinkAppUrl(protectedEvent.eventId, query ?? {})('eiosurlxtcar')}
          androidAppUrl={getBaseDeepLinkAppUrl(protectedEvent.eventId, query ?? {})('eandurlxtcar')}
          twitterCard={TwitterCard.SummaryLargeImage}
        />
        <Header globalHeaderProps={globalHeaderProps} />
        {isRefreshingProtectedEditorContext && (
          <NotificationBannerFrame testId="protected-editor-refresh-banner">
            <EditorRefreshBannerContent />
          </NotificationBannerFrame>
        )}
        <ProtectedEventGate event={protectedEvent} />
      </>
    );
  }
  return <EventListingPage {...props} />;
}
